import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, Filter, ArrowLeft, User,
  MessageSquare, Clock, Calendar, Phone,
  Mail, ChevronLeft, ChevronRight, X, Check,
  Star, FileText, Send, Download, Trash,
  AlertTriangle, MoreVertical, Bell, Info, Flag, Shield
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { updateChat, useAppDispatch, useAppSelector } from "../../redux";
import { getAllAdminChats, getAdminChatConversations, getAdminChatStatistics, flagChatMessage, blockChatConversation, createConversation } from "../../redux";
import { ChatStatusEnum } from "../../modules/shared/model/ChatInterfaces";
import { useWebSocket } from "../../hooks/useWebSocket";

// Chat Management Component
const ChatManagement = () => {
  // Redux hooks
  const dispatch = useAppDispatch();
  const { adminChats, conversations, adminStatistics, error } = useAppSelector((state) => state.chat);

  // State management
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [serviceTypeFilter, setServiceTypeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [selectedDateRange, setSelectedDateRange] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMessageActions, setSelectedMessageActions] = useState(null);
  const [adminNote, setAdminNote] = useState("");
  const [adminMessage, setAdminMessage] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [isLoadingStatistics, setIsLoadingStatistics] = useState(false);
  const [isLoadingChats, setIsLoadingChats] = useState(true);
  const { user } = useAppSelector((state) => state.auth);

  // WebSocket integration
  const { sendMessage: sendWebSocketMessage } = useWebSocket({});

  // Handle responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize(); // Set initial value
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      // Reset to page 1 when search changes
      if (searchQuery !== debouncedSearchQuery) {
        setCurrentPage(1);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch chats on component mount, when page changes, or when debounced search changes
  useEffect(() => {
    const fetchChats = async () => {
      setIsLoadingChats(true);
      try {
        console.log("Fetching chats for page:", currentPage, "with search:", debouncedSearchQuery);
        
        const filters = debouncedSearchQuery.trim() 
          ? { keyword: debouncedSearchQuery.trim() } 
          : undefined;

        await dispatch(getAllAdminChats({ 
          page: currentPage, 
          limit: 10, 
          isShowLoader: false,
          filters 
        })).unwrap();
      } catch (error) {
        console.error("Failed to fetch chats:", error);
      } finally {
        setIsLoadingChats(false);
      }
    };

    fetchChats();
  }, [dispatch, currentPage, debouncedSearchQuery]);

  // Fetch statistics on component mount and when page changes
  useEffect(() => {
    const fetchStatistics = async () => {
      setIsLoadingStatistics(true);
      try {
        await dispatch(getAdminChatStatistics({ isShowLoader: false })).unwrap();
      } catch (error) {
        console.error("Failed to fetch statistics:", error);
      } finally {
        setIsLoadingStatistics(false);
      }
    };

    fetchStatistics();
  }, [dispatch, currentPage]); // Re-fetch when page changes to keep stats updated

  // Load messages when a conversation is selected
  useEffect(() => {
    const fetchConversationMessages = async () => {
      if (!selectedConversation) return;

      setIsLoadingConversations(true);
      setConversationMessages([]); // Clear previous messages
      
      try {
        console.log("Fetching conversations for chat:", selectedConversation.id);
        await dispatch(getAdminChatConversations({ chatId: selectedConversation.id, isShowLoader: false })).unwrap();
      } catch (error) {
        console.error("Failed to fetch conversations:", error);
        setConversationMessages([]);
      } finally {
        setIsLoadingConversations(false);
      }
    };

    fetchConversationMessages();
  }, [dispatch, selectedConversation]);

  // Update conversation messages when real conversations are loaded
  useEffect(() => {
    if (conversations && Array.isArray(conversations) && conversations.length > 0) {
      console.log("Real conversations loaded:", conversations);
      // Transform API conversations to display format
      const transformedMessages = conversations.map((conv, index) => ({
        id: conv.id || index,
        text: conv.message || "No message content",
        timestamp: formatTimestamp(conv.createdAt),
        sender: (conv.sender as any)?.role || "unknown",
        senderName: conv.sender ? `${(conv.sender as any).firstName || ""} ${(conv.sender as any).lastName || ""}`.trim() || "Unknown User" : "Unknown User",
        senderAvatar: (conv.sender as any)?.photo?.path || (conv.sender as any)?.photo?.url || null,
        isRead: conv.isRead || false,
        attachments: conv.attachments || []
      }));
      setConversationMessages(transformedMessages);
    }
  }, [conversations]);

  // Helper function to format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Unknown";
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true
      });
    } else if (diffInHours < 48) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric"
      });
    }
  };

  // Transform real chat data to match the expected format
  const transformChatToConversation = useCallback((chat) => {
    // Find participants by role or use first two participants
    const participants = chat.participants || [];
    let professional = participants.find(p => p.role?.name === "professional" || p.role?.id === 2);
    let client = participants.find(p => p.role?.name === "client" || p.role?.id === 1);

    // If we can't determine by role, use first two participants
    if (!professional && participants.length > 0) {
      professional = participants[0];
    }
    if (!client && participants.length > 1) {
      client = participants[1];
    } else if (!client && participants.length === 1) {
      client = participants[0];
    }

    // Get service type from contextType
    const getServiceType = (contextType) => {
      if (!contextType) return "standard";
      if (typeof contextType === "object") {
        return contextType.id === 1 ? "emergency" : (contextType.id === 3 ? "international" : "standard");
      }
      return contextType === 1 ? "emergency" : (contextType === 3 ? "international" : "standard");
    };

    // Get status from chat status enum
    const getChatStatus = (chatStatus) => {
      if (!chatStatus) return "active";
      const statusId = typeof chatStatus === "object" ? chatStatus.id : chatStatus;
      switch (Number(statusId)) {
        case ChatStatusEnum.flagged:
          return "flagged";
        case ChatStatusEnum.blocked:
          return "blocked";
        case ChatStatusEnum.active:
        default:
          return "active";
      }
    };

    return {
      id: chat.id,
      professional: {
        id: professional?.id || "unknown",
        name: professional ? `${professional.firstName || "Unknown"} ${professional.lastName || "User"}`.trim() : "Unknown Professional",
        service: chat.contextType?.name || "General Service",
        avatar: professional?.photo?.path || null,
        isOnline: professional?.isOnline || false,
        lastSeen: professional?.lastSeen || "Unknown",
        role: professional?.role?.name || "professional"
      },
      client: {
        id: client?.id || "unknown",
        name: client ? `${client.firstName || "Unknown"} ${client.lastName || "User"}`.trim() : "Unknown Client",
        avatar: client?.photo?.path || null,
        isOnline: client?.isOnline || false,
        lastSeen: client?.lastSeen || "Unknown",
        role: client?.role?.name || "client"
      },
      lastMessage: {
        text: chat.lastMessage || "No messages yet",
        timestamp: formatTimestamp(chat.lastMessageAt),
        sender: "unknown",
        isRead: true
      },
      unreadCount: chat.unreadCount ? Object.values(chat.unreadCount).reduce((a: number, b: any) => a + (typeof b === "number" ? b : 0), 0) : 0,
      started: formatTimestamp(chat.createdAt),
      status: getChatStatus(chat.status),
      serviceType: getServiceType(chat.contextType),
      rating: 0,
      isFlagged: Number(chat.status?.id) === ChatStatusEnum.flagged,
      isBlocked: Number(chat.status?.id) === ChatStatusEnum.blocked,
      participants: chat.participants || [],
      chatStatus: chat.status // Keep original status for updates
    };
  }, []);

  // Transform admin chats to conversation format - memoized to prevent re-renders
  const allConversations = useMemo(() => {
    if (!adminChats?.data || adminChats.data.length === 0) {
      return [];
    }
    return adminChats.data.map(transformChatToConversation);
  }, [adminChats?.data, transformChatToConversation]);

  // Filter conversations based on UI filters (search is handled by backend) - memoized
  const filteredConversations = useMemo(() => {
    return allConversations.filter(conversation => {
      const matchesStatus =
        statusFilter === "all" ||
        conversation.status === statusFilter;

      const matchesServiceType =
        serviceTypeFilter === "all" ||
        conversation.serviceType === serviceTypeFilter;

      return matchesStatus && matchesServiceType;
    });
  }, [allConversations, statusFilter, serviceTypeFilter]);

  // Sort filtered conversations - memoized
  const sortedConversations = useMemo(() => {
    return [...filteredConversations].sort((a, b) => {
      switch (sortBy) {
        case "recent":
          return Number(b.id) - Number(a.id);
        case "unread":
          return Number(b.unreadCount) - Number(a.unreadCount);
        case "flagged":
          return b.isFlagged ? -1 : a.isFlagged ? 1 : 0;
        default:
          return 0;
      }
    });
  }, [filteredConversations, sortBy]);

  // Auto-select first conversation on load
  useEffect(() => {
    if (sortedConversations.length > 0 && !selectedConversation) {
      setSelectedConversation(sortedConversations[0]);
    }
  }, [sortedConversations, selectedConversation]);

  // Update selected conversation when data changes (e.g., after status update)
  useEffect(() => {
    if (selectedConversation && allConversations.length > 0) {
      const updatedConversation = allConversations.find(c => c.id === selectedConversation.id);
      if (updatedConversation) {
        setSelectedConversation(updatedConversation);
      }
    }
  }, [allConversations]);

  // Get status badge variant - useCallback to prevent re-renders
  const getStatusBadge = useCallback((status) => {
    switch (status) {
      case "active":
        return { variant: "default", className: "bg-green-50 text-green-700" };
      case "flagged":
        return { variant: "default", className: "bg-yellow-50 text-yellow-700" };
      case "blocked":
        return { variant: "default", className: "bg-red-50 text-red-700" };
      default:
        return { variant: "default", className: "bg-gray-50 text-gray-700" };
    }
  }, []);

  // Get service type badge - useCallback to prevent re-renders
  const getServiceBadge = useCallback((type) => {
    switch (type) {
      case "emergency":
        return { variant: "default", className: "bg-red-50 text-red-700" };
      case "standard":
        return { variant: "default", className: "bg-blue-50 text-blue-700" };
      case "international":
        return { variant: "default", className: "bg-purple-50 text-purple-700" };
      default:
        return { variant: "default", className: "bg-gray-50 text-gray-700" };
    }
  }, []);

  // Helper function for status name conversion
  const getStatusName = useCallback((statusId: ChatStatusEnum) => {
    switch (Number(statusId)) {
      case ChatStatusEnum.flagged:
        return "flagged";
      case ChatStatusEnum.blocked:
        return "blocked";
      case ChatStatusEnum.active:
      default:
        return "active";
    }
  }, []);

  // Handle chat status update
  const handleChatStatusUpdate = useCallback(async (chatId: string, newStatus: ChatStatusEnum) => {
    try {
      const chatToUpdate = adminChats?.data?.find(c => c.id === chatId);
      if (!chatToUpdate) {
        console.error("Chat not found");
        return;
      }

      await dispatch(updateChat({
        chatId: chatId,
        data: {
          contextType: { id: Number(chatToUpdate.contextType?.id || 2) },
          status: { id: newStatus },
          unreadCount: chatToUpdate.unreadCount as Record<string, number>,
          participants: chatToUpdate.participants?.map(p => ({ id: p.id })),
          contextId: chatToUpdate.contextId,
          lastMessageAt: chatToUpdate.lastMessageAt,
          lastMessage: chatToUpdate.lastMessage
        }
      }));

      // Update selected conversation status immediately for UI responsiveness
      if (selectedConversation && selectedConversation.id === chatId) {
        setSelectedConversation({
          ...selectedConversation,
          status: getStatusName(newStatus),
          isFlagged: Number(newStatus) === ChatStatusEnum.flagged,
          isBlocked: Number(newStatus) === ChatStatusEnum.blocked,
          chatStatus: { id: newStatus }
        });
      }

      // Send WebSocket notification to all chat participants about status change
      try {
        const participantIds = chatToUpdate.participants?.map(p => p.id).filter(Boolean) || [];
        if (participantIds.length > 0 && sendWebSocketMessage) {
          await sendWebSocketMessage({
            receivers: participantIds,
            sender: user?.id || "admin",
            type: "chat_status_updated",
            detail: {
              chatId: chatId,
              newStatus: newStatus,
              statusName: getStatusName(newStatus),
              updatedBy: user?.id,
              timestamp: new Date().toISOString()
            }
          });
          console.log("✅ WebSocket notification sent for chat status update");
        }
      } catch (wsError) {
        console.warn("⚠️ Failed to send WebSocket notification for status update:", wsError);
        // Don't fail the operation if WebSocket fails
      }

      // Refresh chat list and statistics
      dispatch(getAllAdminChats({ page: currentPage, limit: 10 }));
      dispatch(getAdminChatStatistics({ isShowLoader: false }));
    } catch (error) {
      console.error("Failed to update chat status:", error);
    }
  }, [dispatch, adminChats, currentPage, selectedConversation, sendWebSocketMessage, user, getStatusName]);

  // Handle message actions
  const handleMessageAction = (action, messageId) => {
    switch (action) {
      case "flag":
        dispatch(flagChatMessage({ messageId, reason: "Inappropriate content" }));
        break;
      case "download":
        // Download message functionality
        console.log("Download message:", messageId);
        break;
      case "delete":
        // Delete message functionality (would need a delete action)
        console.log("Delete message:", messageId);
        break;
      default:
        break;
    }
    setSelectedMessageActions(null);
  };

  // Handle conversation actions - useCallback to prevent re-renders
  const handleConversationAction = useCallback((action) => {
    if (!selectedConversation) return;

    switch (action) {
      case "download":
        // Export chat functionality
        console.log("Export chat:", selectedConversation.id);
        break;
      case "flag":
        handleChatStatusUpdate(selectedConversation.id, ChatStatusEnum.flagged);
        break;
      case "block":
        handleChatStatusUpdate(selectedConversation.id, ChatStatusEnum.blocked);
        break;
      case "unblock":
        handleChatStatusUpdate(selectedConversation.id, ChatStatusEnum.active);
        break;
      case "unflag":
        handleChatStatusUpdate(selectedConversation.id, ChatStatusEnum.active);
        break;
      default:
        break;
    }
  }, [selectedConversation, handleChatStatusUpdate]);

  // Handle sending admin message
  const handleSendAdminMessage = useCallback(async () => {
    if (!adminMessage.trim() || !selectedConversation || !user?.id) return;

    setIsSendingMessage(true);

    // Store message for potential error recovery
    const messageToSend = adminMessage.trim();

    try {
      // Create conversation payload
      const conversationPayload = {
        isRead: false,
        message: messageToSend,
        sender: { id: user.id },
        chatId: { id: selectedConversation.id },
        attachments: []
      };

      // Find the chat and update it
      const chat = adminChats?.data?.find(c => c.id === selectedConversation.id);
      if (chat) {
        const updatedUnreadCount = { ...chat.unreadCount };

        // Set current user's unread count to 0 (they've read their own message)
        updatedUnreadCount[user.id] = 0;

        // Increment unread count for other participants
        chat.participants?.forEach((participant: any) => {
          if (participant.id !== user.id) {
            updatedUnreadCount[participant.id] = (updatedUnreadCount[participant.id] || 0) + 1;
          }
        });

        const isParticipant = chat.participants?.find(p => p.id === user.id);
        const participants = [...(chat.participants || [])];
        if (!isParticipant) {
          participants.push({ id: user.id } as any);
        }

        // Update chat with new message info
        await dispatch(updateChat({
          chatId: chat.id,
          data: {
            contextType: { id: Number(chat.contextType?.id || 2) },
            status: chat.status ? { id: Number(chat.status.id) } : undefined,
            lastMessage: messageToSend,
            participants: participants.map(p => ({ id: p.id })),
            lastMessageAt: new Date().toISOString(),
            unreadCount: updatedUnreadCount,
          }
        })).unwrap();
      }

      // Send the message
      await dispatch(createConversation({ data: conversationPayload })).unwrap();

      // Clear input immediately after successful send
      setAdminMessage("");

      // Add optimistic message to local state
      const newMessage = {
        id: Date.now(),
        text: messageToSend,
        timestamp: new Date().toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true
        }),
        sender: "admin",
        senderName: user?.firstName || user?.email || "Admin",
        senderAvatar: user?.photo?.path || null,
        isRead: true
      };

      setConversationMessages(prev => [...prev, newMessage]);

      // Refresh conversations to get updated data from server
      await dispatch(getAdminChatConversations({ chatId: selectedConversation.id, isShowLoader: false })).unwrap();

    } catch (error) {
      console.error("Failed to send admin message:", error);
      // Restore message if sending failed
      setAdminMessage(messageToSend);
      alert("Failed to send message. Please try again.");
    } finally {
      setIsSendingMessage(false);
    }
  }, [adminMessage, selectedConversation, user, adminChats, dispatch]);

  // Render conversation item
  const renderConversationItem = (conversation) => (
    <div
      key={conversation.id}
      onClick={() => setSelectedConversation(conversation)}
      className={`border-b last:border-b-0 p-3 sm:p-4 hover:bg-gray-50 cursor-pointer transition-colors ${selectedConversation?.id === conversation.id ? "bg-blue-50" : ""
        }`}
    >
      {/* Professional Section */}
      <div className="flex items-center justify-between mb-3" style={{ textAlign: "left" }}>
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="relative flex-shrink-0">
            {conversation.professional.avatar ? (
              <img
                src={conversation.professional.avatar}
                alt={conversation.professional.name}
                className="w-10 h-10 rounded-full object-cover border-2 border-blue-200"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center border-2 border-blue-200">
                <User className="h-5 w-5 text-blue-600" />
              </div>
            )}
            {conversation.professional.isOnline && (
              <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-sm truncate">
                {conversation.professional.name}
              </span>
              <Badge variant="default" className="text-xs bg-blue-50 text-blue-700 flex-shrink-0">
                {conversation.professional.role || "Professional"}
              </Badge>
            </div>
            <div className="text-xs text-gray-500 truncate">
              {conversation.professional.service}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-2">
          <span className="text-xs text-gray-500 whitespace-nowrap">{conversation.lastMessage.timestamp}</span>
          {conversation.unreadCount > 0 && (
            <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
              {conversation.unreadCount}
            </span>
          )}
        </div>
      </div>

      {/* Client Section */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="relative flex-shrink-0">
            {conversation.client.avatar ? (
              <img
                src={conversation.client.avatar}
                alt={conversation.client.name}
                className="w-8 h-8 rounded-full object-cover border-2 border-green-200"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center border-2 border-green-200">
                <User className="h-4 w-4 text-green-600" />
              </div>
            )}
            {conversation.client.isOnline && (
              <span className="absolute bottom-0 right-0 w-2 h-2 bg-green-500 border border-white rounded-full"></span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700 truncate">
                {conversation.client.name}
              </span>
              <Badge variant="default" className="text-xs bg-green-50 text-green-700 flex-shrink-0">
                {conversation.client.role || "Client"}
              </Badge>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
          <Badge {...getStatusBadge(conversation.status)} className="text-xs">
            {conversation.status.charAt(0).toUpperCase() + conversation.status.slice(1)}
          </Badge>
          <Badge {...getServiceBadge(conversation.serviceType)} className="text-xs">
            {conversation.serviceType.charAt(0).toUpperCase() + conversation.serviceType.slice(1)}
          </Badge>
          {conversation.isFlagged && (
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
          )}
        </div>
      </div>

      {/* Last Message */}
      <div className="mt-2 pl-11">
        <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">
          <span className="font-medium">
            {conversation.lastMessage.sender === "professional"
              ? conversation.professional.name.split(" ")[0]
              : conversation.lastMessage.sender === "client"
                ? conversation.client.name.split(" ")[0]
                : "Admin"
            }:
          </span>{" "}
          {conversation.lastMessage.text}
        </p>
      </div>

      {/* Participants Count */}
      {conversation.participants && conversation.participants.length > 0 && (
        <div className="mt-2 pl-11 text-xs text-gray-500">
          <User className="h-3 w-3 inline mr-1" />
          <span className="font-medium">{conversation.participants.length} participants</span>
        </div>
      )}
    </div>
  );

  // Render messages in conversation
  const renderMessage = (message) => {
    const isAdmin = message.sender === "admin";
    const isClient = message.sender === "client";
    const isProfessional = message.sender === "professional";

    return (
      <div
        key={message.id}
        className={`flex ${isClient ? "justify-start" : "justify-end"} mb-3 sm:mb-4 relative group`}
      >
        <div className="flex items-start max-w-[80%] sm:max-w-[75%]" style={{ textAlign: "left" }}>
          <div className="flex-1">
            {/* Sender name and timestamp */}
            <div className="text-xs text-gray-500 mb-1 flex items-center gap-2">
              <span className="font-medium text-gray-700">{message.senderName}</span>
              <span>•</span>
              <span>{message.timestamp}</span>
            </div>

            {/* Message content */}
            <div
              className={`px-3 py-2 sm:px-4 sm:py-2 rounded-lg ${isClient
                ? "bg-gray-100 text-gray-900"
                : isAdmin
                  ? "bg-purple-600 text-white"
                  : "bg-blue-600 text-white"
                }`}
            >
              <p className="text-xs sm:text-sm whitespace-pre-wrap">{message.text}</p>
            </div>
          </div>

          {/* Avatar on the right side for all messages */}
          <div className="ml-2 mt-1 flex-shrink-0">
            {message.senderAvatar ? (
              <img
                src={message.senderAvatar}
                alt={message.senderName}
                className="w-6 h-6 sm:w-8 sm:h-8 rounded-full object-cover border-2 border-white shadow-sm"
              />
            ) : (
              <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gray-200 flex items-center justify-center border-2 border-white shadow-sm">
                <User className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
              </div>
            )}
          </div>
        </div>

        {/* Message actions for admin - only show for non-admin messages */}
        {/* {!isAdmin && (
          <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 mr-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedMessageActions(message.id);
              }}
              className="p-1 hover:bg-gray-200 rounded-full"
            >
              <MoreVertical className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />
            </button>

            {selectedMessageActions === message.id && (
              <div className="absolute right-0 mt-1 w-32 sm:w-40 bg-white rounded-md shadow-lg z-10 border">
                <div className="py-1">
                  <button
                    onClick={() => handleMessageAction("flag", message.id)}
                    className="flex items-center w-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-left text-gray-700 hover:bg-gray-100"
                  >
                    <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4 mr-2 text-red-500" />
                    Flag message
                  </button>
                  <button
                    onClick={() => handleMessageAction("download", message.id)}
                    className="flex items-center w-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-left text-gray-700 hover:bg-gray-100"
                  >
                    <Download className="h-3 w-3 sm:h-4 sm:w-4 mr-2 text-gray-500" />
                    Download
                  </button>
                  <button
                    onClick={() => handleMessageAction("delete", message.id)}
                    className="flex items-center w-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-left text-red-600 hover:bg-gray-100"
                  >
                    <Trash className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        )} */}
      </div>
    );
  };

  // Close conversation detail view and return to list
  const handleBackToList = () => {
    setSelectedConversation(null);
    setConversationMessages([]);
    setAdminNote("");
  };

  // Handle back to admin dashboard (previous page in history)
  const handleBackToDashboard = () => {
    window.history.back();
  };

  // Handle admin message input change - useCallback to prevent re-renders
  const handleAdminMessageChange = useCallback((e) => {
    setAdminMessage(e.target.value);
  }, []);

  // Handle admin note change - useCallback to prevent re-renders
  const handleAdminNoteChange = useCallback((e) => {
    setAdminNote(e.target.value);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50" style={{ textAlign: "left" }}>
      <main className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8">
        <div className="mb-4 sm:mb-6 flex items-center gap-2 sm:gap-4">
          <Button
            onClick={handleBackToDashboard}
            className="p-1 sm:p-2 hover:bg-gray-100 rounded-lg"
            variant="ghost"
          >
            <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
          </Button>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">Chat Management</h1>
        </div>
        <p className="text-sm sm:text-base text-gray-600 ml-8 sm:ml-10">
          View and manage conversations between clients and professionals
        </p>

        {/* Show loader while initial chats are loading */}
        {isLoadingChats && !adminChats ? (
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Loading Chat Management</h3>
              <p className="text-gray-600">Please wait while we fetch your conversations...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-6 sm:mt-8">
          <Card className="p-3 sm:p-4 relative">
            <h3 className="text-xs sm:text-sm font-medium text-gray-500 mb-1 sm:mb-2">Active Conversations</h3>
            <div className="flex justify-between items-center">
              {isLoadingStatistics ? (
                <div className="animate-pulse h-8 w-16 bg-gray-200 rounded"></div>
              ) : (
                <p className="text-lg sm:text-2xl font-bold text-gray-900">
                  {adminStatistics?.activeChats ?? 0}
                </p>
              )}
              <MessageSquare className="h-6 w-6 sm:h-8 sm:w-8 text-blue-100" />
            </div>
            <p className="text-xs sm:text-sm text-gray-600 mt-1 sm:mt-2">
              {isLoadingStatistics ? (
                <span className="animate-pulse">Loading...</span>
              ) : (
                `Total: ${adminStatistics?.totalChats ?? 0} chats`
              )}
            </p>
          </Card>

          <Card className="p-3 sm:p-4 relative">
            <h3 className="text-xs sm:text-sm font-medium text-gray-500 mb-1 sm:mb-2">Flagged Conversations</h3>
            <div className="flex justify-between items-center">
              {isLoadingStatistics ? (
                <div className="animate-pulse h-8 w-16 bg-gray-200 rounded"></div>
              ) : (
                <p className="text-lg sm:text-2xl font-bold text-gray-900">
                  {adminStatistics?.flaggedChats ?? 0}
                </p>
              )}
              <AlertTriangle className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-100" />
            </div>
            <p className="text-xs sm:text-sm text-gray-600 mt-1 sm:mt-2">
              {isLoadingStatistics ? (
                <span className="animate-pulse">Loading...</span>
              ) : (
                "Requires monitoring"
              )}
            </p>
          </Card>

          <Card className="p-3 sm:p-4 relative">
            <h3 className="text-xs sm:text-sm font-medium text-gray-500 mb-1 sm:mb-2">Blocked Conversations</h3>
            <div className="flex justify-between items-center">
              {isLoadingStatistics ? (
                <div className="animate-pulse h-8 w-16 bg-gray-200 rounded"></div>
              ) : (
                <p className="text-lg sm:text-2xl font-bold text-gray-900">
                  {adminStatistics?.blockedChats ?? 0}
                </p>
              )}
              <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-red-100" />
            </div>
            <p className="text-xs sm:text-sm text-gray-600 mt-1 sm:mt-2">
              {isLoadingStatistics ? (
                <span className="animate-pulse">Loading...</span>
              ) : (
                "Restricted access"
              )}
            </p>
          </Card>

          <Card className="p-3 sm:p-4 relative">
            <h3 className="text-xs sm:text-sm font-medium text-gray-500 mb-1 sm:mb-2">Avg. Response Time</h3>
            <div className="flex justify-between items-center">
              {isLoadingStatistics ? (
                <div className="animate-pulse h-8 w-20 bg-gray-200 rounded"></div>
              ) : (
                <p className="text-lg sm:text-2xl font-bold text-gray-900">
                  {adminStatistics?.avgResponseTime ?? "0m"}
                </p>
              )}
              <Clock className="h-6 w-6 sm:h-8 sm:w-8 text-green-100" />
            </div>
            <p className="text-xs sm:text-sm text-gray-600 mt-1 sm:mt-2">
              {isLoadingStatistics ? (
                <span className="animate-pulse">Loading...</span>
              ) : (
                `Based on ${adminStatistics?.totalChats ?? 0} chats`
              )}
            </p>
          </Card>
        </div>



        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-3 h-full">
            {/* Conversations List */}
            <div className={`border-r ${selectedConversation && !isMobile ? "hidden lg:block" : selectedConversation ? "hidden" : "block"}`}>
              {/* Search and Filters */}
              <div className="p-3 sm:p-4 border-b">
                <div className="relative mb-3 sm:mb-4">
                  <input
                    type="text"
                    placeholder="Search by name, email, or message..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 sm:pl-10 pr-10 sm:pr-12 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                  />
                  <Search className="absolute left-3 top-2.5 text-gray-400 h-4 w-4 sm:h-5 sm:w-5" />
                  
                  {/* Loading indicator or Clear button */}
                  <div className="absolute right-3 top-2.5">
                    {searchQuery && searchQuery !== debouncedSearchQuery ? (
                      <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-blue-500"></div>
                    ) : searchQuery ? (
                      <button
                        onClick={() => setSearchQuery("")}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        title="Clear search"
                      >
                        <X className="h-4 w-4 sm:h-5 sm:w-5" />
                      </button>
                    ) : null}
                  </div>
                </div>
                
                {/* Search info */}
                {debouncedSearchQuery && (
                  <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-blue-700">
                        <span className="font-medium">Searching for:</span> &quot;{debouncedSearchQuery}&quot;
                        {adminChats?.totalCount !== undefined && (
                          <span className="ml-2 font-semibold">
                            {adminChats.totalCount} result{adminChats.totalCount !== 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => setSearchQuery("")}
                        className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row justify-between items-center mb-3 sm:mb-4 gap-2">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-blue-600"
                  >
                    <Filter className="h-3 w-3 sm:h-4 sm:w-4" />
                    {showFilters ? "Hide Filters" : "Show Filters"}
                  </button>

                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full sm:w-auto text-xs sm:text-sm border rounded-md px-2 sm:px-3 py-1"
                  >
                    <option value="recent">Most Recent</option>
                    <option value="unread">Unread First</option>
                    <option value="flagged">Flagged First</option>
                  </select>
                </div>

                {showFilters && (
                  <div className="space-y-3 sm:space-y-4 pb-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                          Status
                        </label>
                        <select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          className="w-full rounded-lg border-gray-300 shadow-sm text-xs sm:text-sm py-1.5 sm:py-2"
                        >
                          <option value="all">All Statuses</option>
                          <option value="active">Active</option>
                          <option value="flagged">Flagged</option>
                          <option value="blocked">Blocked</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                          Service Type
                        </label>
                        <select
                          value={serviceTypeFilter}
                          onChange={(e) => setServiceTypeFilter(e.target.value)}
                          className="w-full rounded-lg border-gray-300 shadow-sm text-xs sm:text-sm py-1.5 sm:py-2"
                        >
                          <option value="all">All Services</option>
                          <option value="emergency">Emergency</option>
                          <option value="standard">Standard</option>
                          <option value="international">International</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        Date Range
                      </label>
                      <select
                        value={selectedDateRange}
                        onChange={(e) => setSelectedDateRange(e.target.value)}
                        className="w-full rounded-lg border-gray-300 shadow-sm text-xs sm:text-sm py-1.5 sm:py-2"
                      >
                        <option value="all">All Time</option>
                        <option value="today">Today</option>
                        <option value="week">This Week</option>
                        <option value="month">This Month</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Conversations List */}
              <div className="divide-y overflow-y-auto max-h-[50vh] sm:max-h-[60vh] md:max-h-[70vh] relative">
                {isLoadingChats ? (
                  <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-10">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-3"></div>
                      <p className="text-sm text-gray-600">Loading conversations...</p>
                    </div>
                  </div>
                ) : null}
                
                {sortedConversations.length > 0 ? (
                  sortedConversations.map(renderConversationItem)
                ) : !isLoadingChats ? (
                  <div className="p-4 sm:p-8 text-center text-gray-500">
                    <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    {debouncedSearchQuery ? (
                      <>
                        <p className="text-sm sm:text-base font-medium text-gray-700 mb-1">
                          No results found for &quot;{debouncedSearchQuery}&quot;
                        </p>
                        <p className="text-xs sm:text-sm text-gray-500 mb-3">
                          Try a different search term or clear the search
                        </p>
                        <button
                          onClick={() => setSearchQuery("")}
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          Clear Search
                        </button>
                      </>
                    ) : (
                      <p className="text-sm sm:text-base">
                        No conversations found matching your filters.
                      </p>
                    )}
                  </div>
                ) : null}
              </div>

              {/* Pagination */}
              <div className="p-3 sm:p-4 border-t flex justify-center gap-1 sm:gap-2 items-center">
                <button
                  className="p-1 sm:p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={currentPage === 1 || isLoadingChats}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>

                <span className="px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm text-gray-600">
                  Page {adminChats?.currentPage || currentPage} of {adminChats?.totalCount ? Math.ceil(adminChats.totalCount / 10) : 1}
                  {adminChats?.totalCount ? ` (${adminChats.totalCount} total)` : ""}
                </span>

                <button
                  className="p-1 sm:p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!adminChats?.hasNextPage || isLoadingChats}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </div>
            </div>

            {/* Conversation Detail */}
            <div className={`lg:col-span-2 ${!selectedConversation ? "hidden lg:flex lg:items-center lg:justify-center" : "block"}`}>
              {selectedConversation ? (
                <div className="flex flex-col h-full">
                  {/* Conversation Header */}
                  <div className="p-3 sm:p-4 border-b flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <button
                        onClick={handleBackToList}
                        className="p-2 hover:bg-gray-100 rounded-lg lg:hidden flex-shrink-0"
                      >
                        <ArrowLeft className="h-5 w-5" />
                      </button>

                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="flex -space-x-2 flex-shrink-0">
                          {selectedConversation.professional.avatar ? (
                            <img
                              src={selectedConversation.professional.avatar}
                              alt={selectedConversation.professional.name}
                              className="w-9 h-9 sm:w-10 sm:h-10 rounded-full border-2 border-white object-cover shadow-sm"
                              title={`${selectedConversation.professional.name} (${selectedConversation.professional.role || "Professional"})`}
                            />
                          ) : (
                            <div 
                              className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-blue-100 flex items-center justify-center border-2 border-white shadow-sm"
                              title={`${selectedConversation.professional.name} (${selectedConversation.professional.role || "Professional"})`}
                            >
                              <User className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                            </div>
                          )}
                          {selectedConversation.client.avatar ? (
                            <img
                              src={selectedConversation.client.avatar}
                              alt={selectedConversation.client.name}
                              className="w-9 h-9 sm:w-10 sm:h-10 rounded-full border-2 border-white object-cover shadow-sm"
                              title={`${selectedConversation.client.name} (${selectedConversation.client.role || "Client"})`}
                            />
                          ) : (
                            <div 
                              className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-green-100 flex items-center justify-center border-2 border-white shadow-sm"
                              title={`${selectedConversation.client.name} (${selectedConversation.client.role || "Client"})`}
                            >
                              <User className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                            </div>
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-base truncate">
                            {selectedConversation.professional.name} & {selectedConversation.client.name}
                          </h3>
                          <div className="flex items-center text-xs text-gray-500 flex-wrap gap-1">
                            <span className="truncate">{selectedConversation.professional.service}</span>
                            <span>•</span>
                            <span className="whitespace-nowrap">Started {selectedConversation.started}</span>
                            {selectedConversation.participants && selectedConversation.participants.length > 0 && (
                              <>
                                <span>•</span>
                                <span className="whitespace-nowrap">{selectedConversation.participants.length} participants</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge {...getStatusBadge(selectedConversation.status)} className="text-xs">
                        {selectedConversation.status.charAt(0).toUpperCase() + selectedConversation.status.slice(1)}
                      </Badge>
                      <Badge {...getServiceBadge(selectedConversation.serviceType)} className="text-xs hidden sm:inline-flex">
                        {selectedConversation.serviceType.charAt(0).toUpperCase() + selectedConversation.serviceType.slice(1)}
                      </Badge>
                      {selectedConversation.isFlagged && (
                        <AlertTriangle className="h-5 w-5 text-yellow-500" />
                      )}
                    </div>
                  </div>

                  {/* Action buttons for admin */}
                  <div className="p-2 sm:p-3 border-b bg-gray-50 flex flex-wrap gap-1 sm:gap-2">
                    <Button
                      variant="outline"
                      className="text-xs sm:text-sm w-full sm:w-auto"
                      onClick={() => handleConversationAction("download")}
                    >
                      <Download className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                      Export Chat
                    </Button>

                    {selectedConversation.status !== "flagged" ? (
                      <Button
                        variant="outline"
                        className="text-xs sm:text-sm w-full sm:w-auto text-yellow-600"
                        onClick={() => handleConversationAction("flag")}
                      >
                        <Flag className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                        Flag for Monitoring
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        className="text-xs sm:text-sm w-full sm:w-auto text-green-600"
                        onClick={() => handleConversationAction("unflag")}
                      >
                        <Check className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                        Unflag
                      </Button>
                    )}

                    {selectedConversation.status !== "blocked" ? (
                      <Button
                        variant="outline"
                        className="text-xs sm:text-sm w-full sm:w-auto text-red-600"
                        onClick={() => handleConversationAction("block")}
                      >
                        <Shield className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                        Block Conversation
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        className="text-xs sm:text-sm w-full sm:w-auto text-green-600"
                        onClick={() => handleConversationAction("unblock")}
                      >
                        <Check className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                        Unblock
                      </Button>
                    )}
                  </div>

                  {/* Messages Container */}
                  <div className="flex-1 p-3 sm:p-4 overflow-y-auto max-h-[40vh] sm:max-h-[50vh] md:max-h-[60vh]">
                    {isLoadingConversations ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                          <p className="text-sm text-gray-500">Loading messages...</p>
                        </div>
                      </div>
                    ) : conversationMessages.length > 0 ? (
                      conversationMessages.map(renderMessage)
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center text-gray-500">
                          <MessageSquare className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                          <p className="text-sm">No messages in this conversation yet</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Admin Notes Section */}
                  <div className="p-3 sm:p-4 border-t bg-gray-50">


                    {/* Admin Message Input */}
                    <div className="border-t pt-3">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                        Send Message as Admin
                      </label>
                      <div className="flex gap-2">
                        <textarea
                          value={adminMessage}
                          onChange={handleAdminMessageChange}
                          placeholder="Type your message here... (Press Enter to send, Shift+Enter for new line)"
                          className="flex-1 p-2 border rounded-lg text-xs sm:text-sm resize-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          rows={2}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              handleSendAdminMessage();
                            }
                          }}
                        />
                        <Button
                          className="text-xs sm:text-sm bg-purple-600 text-white px-3 py-2 self-end hover:bg-purple-700 transition-colors"
                          onClick={handleSendAdminMessage}
                          disabled={!adminMessage.trim() || isSendingMessage}
                        >
                          {isSendingMessage ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          ) : (
                            <>
                              <Send className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                              Send
                            </>
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Admin messages will be visible to all participants in this conversation.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center p-6 sm:p-8">
                  <MessageSquare className="h-10 w-10 sm:h-12 sm:w-12 text-gray-300 mx-auto mb-3 sm:mb-4" />
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-1 sm:mb-2">No conversation selected</h3>
                  <p className="text-xs sm:text-sm text-gray-500 max-w-md mx-auto">
                    Select a conversation from the list to view details, monitor communications, and take administrative actions if needed.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>


          </>
        )}

        {/* Help Section */}
        <div className="mt-6 sm:mt-8">
          <Alert className="bg-blue-50 border-blue-200">
            <div className="flex gap-2 sm:gap-3 items-start">
              <Info className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 mt-0.5" />
              <AlertDescription className="text-blue-700">
                <p className="font-medium text-xs sm:text-sm mb-1">Admin Chat Management Guide</p>
                <p className="text-xs sm:text-sm">
                  This panel lets you monitor conversations, intervene when necessary, flag problematic messages, and assist with dispute resolution. For training on handling specific situations, check the Admin Knowledge Base.
                </p>
              </AlertDescription>
            </div>
          </Alert>
        </div>
      </main>
    </div>
  );
};

export default ChatManagement;